import { useEffect, useState } from "react";
import {
  filterNavItemByPermission,
  getAssistantNavSections,
  getAccountNavItems,
  getAdminNavItems
} from "src/layout/Navigation/navigationItems";
import { NAVIGATION_MODES } from "src/layout/Navigation/NavigationBar.consts";
import { hasPermissionSelector, isOutboundAgentSelector, isVoiceAgentSelector, selectedAccountIdSelector } from "src/stores/selectors/appSelectors";
import useAppStore from "src/stores/appStore";
import { useStatusBarDisplayed } from "@hooks";
import { useMediaQuery, useTheme } from "@mui/material";
import { useParams } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import { CAMPAIGN_MANAGER, BRANCH_DEPLOYER } from "@consts";
import { isNotCodefullAgent, isProd } from "@utils";

const NAV_OPEN_STATE_KEY = "navigationOpenState";

const getNavigationOpenState = () => {
  const savedState = localStorage.getItem(NAV_OPEN_STATE_KEY);
  if (savedState === null || savedState === "undefined") {
    return true;
  }
  return JSON.parse(savedState);
};

const useNavigationProps = (mode = NAVIGATION_MODES.ASSISTANT) => {
  const { assistantId } = useParams();
  const [open, setOpen] = useState(getNavigationOpenState);
  const theme = useTheme();
  const accountId = useAppStore(selectedAccountIdSelector);
  const isOutboundAgent = useAppStore(isOutboundAgentSelector);
  const isVoiceAgent = useAppStore(isVoiceAgentSelector);
  const hasPermission = useAppStore(hasPermissionSelector);
  const showStatusBar = useStatusBarDisplayed();
  const isMediumSizeDevice = useMediaQuery(theme.breakpoints.between("xs", "md"));
  const flags = useFlags();
  const filterNavItemByAccountPermission = filterNavItemByPermission(hasPermission);
  const filterNavItemByAgentType = (navItem) => !navItem.isCodelessRoute ||
    (navItem.isCodelessRoute && isNotCodefullAgent(assistantId));
  const filterNavItemByCampaignManager = (navItem) =>
    navItem.key !== CAMPAIGN_MANAGER || (isOutboundAgent);
  const filterNavItemByFeatureFlag = (navItem) =>
    !navItem.requiredFlag || Boolean(flags[navItem.requiredFlag]);
  const filterNavItemByVoiceAgent = (navItem) =>
    !navItem.voiceOnly || isVoiceAgent;

  let sections = [];
  let showAssistantSelector = false;

  if (mode === NAVIGATION_MODES.ASSISTANT && assistantId) {
    sections = getAssistantNavSections(accountId, assistantId)
      .filter(section => !section.requiredPermission || hasPermission(section.requiredPermission))
      .map(section => ({
        ...section,
        items: section.items
          .filter(filterNavItemByAccountPermission)
          .filter(filterNavItemByAgentType)
          .filter(filterNavItemByCampaignManager)
          .filter(filterNavItemByFeatureFlag)
          .filter(filterNavItemByVoiceAgent)
      }))
      .filter(section => section.items.length > 0);

    showAssistantSelector = true;
  } else if (mode === NAVIGATION_MODES.ACCOUNT && accountId) {
    const items = getAccountNavItems(accountId)
      .filter(filterNavItemByAccountPermission);
    sections = [{ key: "main", title: null, items }];
  } else if (mode === NAVIGATION_MODES.ADMIN) {
    const items = getAdminNavItems()
      .filter(filterNavItemByAccountPermission)
      .filter((item) => item.key !== BRANCH_DEPLOYER || (!isProd() && item.key === BRANCH_DEPLOYER));
    sections = [{ key: "main", title: null, items }];
  }

  useEffect(() => {
    if (isMediumSizeDevice) {
      setOpen(false);
    } else {
      setOpen(getNavigationOpenState());
    }
  }, [isMediumSizeDevice]);

  const handleSetOpen = (value) => {
    setOpen(value);
    localStorage.setItem(NAV_OPEN_STATE_KEY, JSON.stringify(value));
  };

  return {
    sections,
    open,
    setOpen: handleSetOpen,
    showStatusBar,
    mode,
    showAssistantSelector,
    showMinimizeButton: !isMediumSizeDevice
  };
};

export default useNavigationProps;
